import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Loader2, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { type ZodType, z } from "zod";

import { MAX_MONTHLY_BUDGET } from "../../constants";

import { Button } from "@/components/ui/v2/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/v2/form";
import { FormattedCurrencyInput } from "@/components/ui/v2/formatted-currency-input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { useAuth } from "@/lib/hooks/useAuth";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { cn } from "@/lib/utils/cn";
import { JobboardCampaignType } from "@/types/response/CampaignJobBoardMonthType";
import { UserRole } from "@/types/user/userType";
import {
  getProRataFirstMonthBudget,
  parseLocalDateOnly,
} from "@/utils/dateUtils";
import { formatCurrency } from "@/utils/valueFormat";

interface BudgetRowProps {
  existingData: JobboardCampaignType;
  isEditing: boolean;
  isEditingSpent: boolean;
  editValue: string;
  isLoading: boolean;
  isNewMonth: boolean;
  isPastMonth: boolean;
  isCurrentMonth: boolean;
  minimumBudget?: number;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onValueChange: (value: string) => void;
  getNumberValue: (value: string) => string;
  startDate?: string;
}

type BudgetFormValues = {
  budget: string;
};

function BudgetRow({
  existingData,
  isEditing,
  isEditingSpent,
  editValue,
  isLoading,
  isNewMonth,
  isPastMonth,
  isCurrentMonth,
  minimumBudget,
  onEdit,
  onSave,
  onCancel,
  onValueChange,
  getNumberValue,
  startDate,
}: BudgetRowProps) {
  const { t } = useTranslation();
  const { canEdit } = usePermissionMatrix();
  const { role } = useAuth();

  const isInvoiced = existingData.isInvoiced === true;
  // A month is prorated exactly when it carries the full monthly budget it was derived from.
  const monthlyBudget = existingData.monthlyBudget;
  const canEditCampaigns = canEdit("campaigns");
  const isBudgetEditable =
    !isInvoiced && !isPastMonth && canEditCampaigns;

  const displayBudget = monthlyBudget ?? existingData.budget;

  const proratedPreview = useMemo(() => {
    if (monthlyBudget === null || !editValue) return null;
    const numValue = Number.parseFloat(editValue);
    if (Number.isNaN(numValue) || numValue <= 0) return null;
    const proRataFrom = parseLocalDateOnly(startDate);
    if (!proRataFrom) return null;
    return getProRataFirstMonthBudget(numValue, proRataFrom);
  }, [monthlyBudget, editValue, startDate]);
  const isAddingNewBudget = isEditing && isNewMonth;
  const showEditForm = isEditing && !isInvoiced;
  const canViewMarginBreakdown =
    (role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN) &&
    existingData.includeMargin;

  const budgetSchema = useMemo(() => {
    let schema: ZodType<string, string> = z
      .string()
      .min(1, {
        message: t("errors.requiredField", {
          fieldName: t("common.budget"),
        }),
      })
      .refine(
        (val) => {
          const numValue = Number.parseFloat(val);
          return !Number.isNaN(numValue) && numValue >= 0;
        },
        {
          message: t("errors.budgetMin", { min: 0 }),
        }
      )
      .refine(
        (val) => {
          if (val.includes(".") || val.includes(",")) {
            return false;
          }
          const numValue = Number.parseFloat(val);
          return Number.isInteger(numValue);
        },
        {
          message: t("errors.integer", { field: t("common.budget") }),
        }
      )
      .refine(
        (val) => {
          const numValue = Number.parseFloat(val);
          return numValue <= MAX_MONTHLY_BUDGET;
        },
        {
          message: t("errors.budgetMax", {
            max: MAX_MONTHLY_BUDGET.toLocaleString(),
          }),
        }
      );

    if (isCurrentMonth && minimumBudget != null && minimumBudget > 0) {
      schema = schema.refine(
        (val) => {
          const numValue = Number.parseFloat(val);
          return !Number.isNaN(numValue) && numValue >= minimumBudget;
        },
        {
          message: t("errors.budgetMin", {
            min: minimumBudget.toLocaleString(),
          }),
        }
      );
    }

    return z.object({ budget: schema });
  }, [t, isCurrentMonth, minimumBudget]);

  const form = useForm<BudgetFormValues>({
    resolver: zodResolver(budgetSchema),
    defaultValues: {
      budget: editValue,
    },
    mode: "onChange",
  });

  // Sync form value with external editValue prop
  useEffect(() => {
    if (isEditing) {
      form.setValue("budget", editValue, { shouldValidate: true });
    }
  }, [editValue, isEditing, form]);

  useEffect(() => {
    if (!isEditing) {
      form.reset({ budget: displayBudget.toString() });
    }
  }, [isEditing, displayBudget, form]);

  // Reset editing state when month is invoiced (e.g. data refreshed after invoice)
  useEffect(() => {
    if (isEditing && isInvoiced) {
      onCancel();
    }
  }, [isEditing, isInvoiced, onCancel]);

  const handleSave = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const formData = form.getValues();
    onValueChange(formData.budget);
    onSave();
  };

  const handleCancel = () => {
    form.reset({ budget: displayBudget.toString() });
    onCancel();
  };

  const handleValueChange = (value: string) => {
    const processedValue = getNumberValue(value);
    form.setValue("budget", processedValue, { shouldValidate: true });
    onValueChange(processedValue);
  };

  return (
    <div className="group/budget-row min-h-9">
      <AnimatePresence mode="wait">
        {showEditForm ? (
          <motion.div
            key="edit"
            initial={{ y: 20, opacity: 0, height: 0 }}
            animate={{ y: 0, opacity: 1, height: "auto" }}
            exit={{ y: 20, opacity: 0, height: 0 }}
            transition={{ duration: 0.1, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSave)}
                className="space-y-2"
              >
                <FormField
                  control={form.control}
                  name="budget"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-foreground">
                            {t("common.editBudget")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FormControl>
                            <FormattedCurrencyInput
                              value={
                                field.value
                                  ? Number.parseFloat(field.value)
                                  : undefined
                              }
                              variant="ghost"
                              suffix={false}
                              caretPosition="right"
                              size="sm"
                              inputMode="numeric"
                              maxValue={MAX_MONTHLY_BUDGET}
                              onChange={(value) => {
                                const processedValue =
                                  value !== undefined ? value.toString() : "";
                                field.onChange(processedValue);
                                handleValueChange(processedValue);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleSave();
                                } else if (e.key === "Escape") {
                                  handleCancel();
                                }
                              }}
                              className="h-9 w-32 text-base font-semibold"
                              placeholder="0"
                              disabled={isLoading}
                              autoFocus
                            />
                          </FormControl>
                          <div className="flex items-center gap-1">
                            <Button
                              type="submit"
                              variant="ghost"
                              size="sm"
                              disabled={isLoading || !form.formState.isValid}
                              className="h-9 w-9 p-0 hover-tint-primary hover:text-primary"
                              title={
                                isAddingNewBudget
                                  ? t("common.create")
                                  : t("common.save")
                              }
                            >
                              {isLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handleCancel}
                              disabled={isLoading}
                              className="h-9 w-9 p-0 hover:bg-destructive/10 hover:text-destructive"
                              title={t("common.cancel")}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      <FormMessage />
                      {proratedPreview != null && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("common.proratedEstimate", {
                            amount: formatCurrency(proratedPreview),
                          })}
                        </p>
                      )}
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </motion.div>
        ) : (
          <motion.div
            key="view"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.1, ease: "easeInOut" }}
            className="flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground">
                {t("common.budget")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {isBudgetEditable && !isEditingSpent ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="flex flex-col gap-0.5 items-end cursor-pointer"
                      role="button"
                      tabIndex={0}
                      onClick={onEdit}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEdit(); } }}
                    >
                      <span
                        className={cn(
                          "text-lg font-semibold tracking-tight transition-all group-hover/budget-row:underline",
                          isInvoiced && "",
                          isNewMonth && "underline"
                        )}
                      >
                        {formatCurrency(displayBudget)}
                        {canViewMarginBreakdown && (
                          <span
                            className="ml-1.5 text-xs font-normal text-muted-foreground"
                            title={t("common.withMargin")}
                          >
                            {t("common.marginAbbr")}
                          </span>
                        )}
                      </span>
                      {monthlyBudget !== null && (
                        <span className="text-xs text-muted-foreground">
                          {t("common.proratedEstimate", {
                            amount: formatCurrency(existingData.budget),
                          })}
                        </span>
                      )}
                      {canViewMarginBreakdown && (
                        <span
                          className="text-xs"
                          title={t("common.withoutMargin")}
                        >
                          {formatCurrency(existingData.invoiceableBudget)}{" "}
                          {t("common.woMargin")}
                        </span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isNewMonth
                      ? t("common.addBudget")
                      : t("common.editBudget")}
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="flex flex-col gap-0.5 items-end">
                  <span
                    className={cn(
                      "text-lg font-semibold tracking-tight",
                      isInvoiced && ""
                    )}
                  >
                    {formatCurrency(displayBudget)}
                    {canViewMarginBreakdown && (
                      <span
                        className="ml-1.5 text-xs font-normal text-muted-foreground"
                        title={t("common.withMargin")}
                      >
                        {t("common.marginAbbr")}
                      </span>
                    )}
                  </span>
                  {monthlyBudget !== null && (
                    <span className="text-xs text-muted-foreground">
                      {t("common.proratedEstimate", {
                        amount: formatCurrency(existingData.budget),
                      })}
                    </span>
                  )}
                  {canViewMarginBreakdown && (
                    <span className="text-xs" title={t("common.withoutMargin")}>
                      {formatCurrency(existingData.invoiceableBudget)}{" "}
                      {t("common.woMargin")}
                    </span>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default BudgetRow;
